/**
 * Pure geometry + labeling for timeline bars: group aspects by block, compute contiguous
 * date runs, map to pixel segments for each visible day column, and expose resize origin
 * dates aligned with that same aspect ordering.
 */
import {
  isDateInInclusiveRange,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { SCHEDULE_BAR } from "../constants";
import { SCHEDULE_BAR_STYLE } from "../style";
import {
  getAspectBlockId,
  getBlockLabel,
} from "./scheduleBlockSelectors";
import { getAssignmentDifference } from "./scheduleWorkerAssignmentDiff";
import { getScheduleBlockRowId } from "./scheduleRowModel";

/**
 * Resize / drag math uses the persisted block span. Aspect rows may be windowed by the
 * schedule API, so they are not authoritative for the full visual block range.
 *
 * @param {Object} block
 * @param {Array<Object>} [workScopeAspects]
 * @returns {{ start: Date, end: Date }}
 */
export function getResizeSessionOrigins(block, workScopeAspects = []) {
  return {
    start: block.startDate,
    end: block.endDate,
  };
}

/**
 * One contiguous run per block (skipped days stay in the bar for hit-testing / toggle).
 * The persisted block span is authoritative because API-loaded aspect rows are windowed.
 *
 * @param {Object} block
 * @param {Array<Object>} sortedAspects Aspects for one block, sorted by date.
 * @returns {Array<{ start: Date, end: Date }>}
 */
function contiguousRunForBlock(block, sortedAspects) {
  if (!block?.startDate || !block?.endDate) {
    if (sortedAspects.length === 0) {
      return [];
    }
    return [
      {
        start: new Date(sortedAspects[0].date),
        end: new Date(sortedAspects[sortedAspects.length - 1].date),
      },
    ];
  }
  if (block.endDate < block.startDate) {
    return [];
  }
  return [
    {
      start: new Date(block.startDate),
      end: new Date(block.endDate),
    },
  ];
}

/**
 * Build render-ready ScopeBars from blocks, per-day aspects, and row layout metadata.
 *
 * @param {Object} params
 * @param {Array<Object>} params.blocks
 * @param {Array<Object>} [params.workScopeAspects]
 * @param {Array<Object>} [params.aspectDays] Legacy alias for `workScopeAspects`.
 * @param {Array<Object>} params.workScopes Catalog scopes for labels.
 * @param {Array<Object>} params.groups Schedule groups for row placement.
 * @param {Array<Object>} params.scheduleRows Flat visible rows.
 * @param {Array<Date>} params.visibleDays Timeline columns.
 * @param {number} params.cellWidth Day column width (px).
 * @param {number} params.rowHeight Base row height (px).
 * @param {Map<string, number>} params.rowTopById Row id → top offset.
 * @param {Map<string|number, number>} params.laneByBlockId Block id → visual lane index.
 * @returns {Array<Object>} ScopeBar render models (`id`, `blockId`, geometry, `label`, …).
 */
export function buildTimelineBars({
  blocks,
  workScopeAspects = [],
  aspectDays,
  workScopes,
  groups,
  scheduleRows,
  visibleDays,
  cellWidth,
  rowHeight,
  rowTopById,
  laneByBlockId,
}) {
  const aspectsList = workScopeAspects.length ? workScopeAspects : aspectDays || [];
  const workScopeById = new Map(workScopes.map((ws) => [ws.id, ws]));
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const rowIndexById = new Map(
    scheduleRows.map((row, idx) => [row.id, idx])
  );

  const aspectsByBlock = new Map();
  for (const day of aspectsList) {
    const bid = getAspectBlockId(day);
    if (bid == null) {
      continue;
    }
    if (!aspectsByBlock.has(bid)) {
      aspectsByBlock.set(bid, []);
    }
    aspectsByBlock.get(bid).push(day);
  }

  const scopeBars = [];

  for (const block of blocks) {
    const targetRowId = getScheduleBlockRowId(block);
    const rowIndex = rowIndexById.get(targetRowId);
    if (rowIndex === undefined) {
      continue;
    }
    const rowTop = rowTopById?.get(targetRowId) ?? rowIndex * rowHeight;
    const lane = laneByBlockId?.get(String(block.id)) ?? 0;

    const label = getBlockLabel(block, workScopeById);

    const groupId = block.scheduleGroup?.id ?? null;
    const group = groupId ? groupById.get(groupId) : null;
    const blockAspects = (aspectsByBlock.get(block.id) || []).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
    const runs = contiguousRunForBlock(block, blockAspects);

    let runIdx = 0;
    for (const { start: startDate, end: endDate } of runs) {
      let firstVisibleIdx = -1;
      let lastVisibleIdx = -1;
      for (let i = 0; i < visibleDays.length; i += 1) {
        if (isDateInInclusiveRange(visibleDays[i], startDate, endDate)) {
          if (firstVisibleIdx === -1) {
            firstVisibleIdx = i;
          }
          lastVisibleIdx = i;
        }
      }

      if (firstVisibleIdx === -1 || lastVisibleIdx === -1) {
        runIdx += 1;
        continue;
      }

      const color = group?.color || SCHEDULE_BAR_STYLE.defaultColor;
      const clippedStart =
        toLocalDateKey(startDate) !==
        toLocalDateKey(visibleDays[firstVisibleIdx]);
      const clippedEnd =
        toLocalDateKey(endDate) !==
        toLocalDateKey(visibleDays[lastVisibleIdx]);

      const runCount = runs.length;
      const aspectsByDayKey = new Map(
        blockAspects.map((a) => [toLocalDateKey(a.date), a])
      );
      const colCount = lastVisibleIdx - firstVisibleIdx + 1;
      const perDayWorkerOverride = [];
      const perDaySkipped = [];
      for (let i = 0; i < colCount; i += 1) {
        const day = visibleDays[firstVisibleIdx + i];
        const asp = day ? aspectsByDayKey.get(toLocalDateKey(day)) : null;
        perDayWorkerOverride.push(
          Boolean(asp && getAssignmentDifference(block, asp, false))
        );
        perDaySkipped.push(Boolean(asp?.skipped));
      }

      scopeBars.push({
        id: `${block.id}-run-${runIdx}`,
        blockId: block.id,
        label,
        left: firstVisibleIdx * cellWidth + SCHEDULE_BAR.leftOffset,
        width: Math.max(
          SCHEDULE_BAR.minWidth,
          (lastVisibleIdx - firstVisibleIdx + 1) * cellWidth -
            SCHEDULE_BAR.widthShrink
        ),
        top: rowTop + lane * rowHeight + SCHEDULE_BAR.topOffset,
        height: Math.max(
          SCHEDULE_BAR.minHeight,
          rowHeight - SCHEDULE_BAR.verticalMargin
        ),
        color,
        clippedStart,
        clippedEnd,
        resizeStart: runIdx === 0,
        resizeEnd: runIdx === runCount - 1,
        firstVisibleIdx,
        lastVisibleIdx,
        perDayWorkerOverride,
        perDaySkipped,
      });
      runIdx += 1;
    }
  }

  return scopeBars.sort((a, b) => {
    if (a.top !== b.top) {
      return a.top - b.top;
    }
    return a.left - b.left;
  });
}
